import '@3dp/ui/shell.css';
import '@3dp/ui/picker.css';   // the font control and its sheet (unify-ui ship 4a): before the tool's own stylesheet
import '@3dp/ui/colours.css';  // the colour control (unify-ui ship 4b): before the tool's own stylesheet too
import './main.css';
import { publishBarHeight, publishHeaderHeight } from '@3dp/ui/bar-height';
import { pageLanguage } from '@3dp/ui/locales';
import { asset } from '@3dp/ui/asset';
import { defaultDoc, normalize, encode, decode, LIMITS } from '@3dp/core/doc';
import { fontKey } from '@3dp/core/fonts/catalog';
import { offerFor, megabytes, listMayHelp, asListed } from '@3dp/core/fonts/choose';
import { PICKER_CORE } from '@3dp/core/fonts/picker-core';
import { LOOKS, applyLook, COLOUR_CORE } from '@3dp/core/filaments';
import { hit, lookLabel } from '@3dp/core/stats';
import { createWorkerClient } from './worker-client.js';
import { createPreview } from './preview3d.js';
import { S } from './strings.js';
import { readShareHash, shareUrl, isLineBrowser } from './share.js';
import { wireLangSwitch } from '@3dp/ui/switch';   // ship 3 (R21): the switch's links carry the design through this tool's own share link
import { mountLangOffer } from '@3dp/ui/offer';
import { esc } from './esc.js';
import { createFontControl } from '@3dp/ui/font-control';
import { createColours } from '@3dp/ui/colours';

publishBarHeight(document.querySelector('.ui-bar'));   // --ui-bar-h: shell.css ends the desktop panel above the fixed bar
publishHeaderHeight(document.querySelector('header.ui-header')); // --ui-header-h: ship 3's taller header row; the desktop panel leaves its room too
const $ = (s) => document.querySelector(s);
const shared = readShareHash(location.hash); let doc = normalize(shared || defaultDoc());

// ship 3 (R21): the language switch and the English-page offer. The drawing in hand and an uploaded
// font never travel in a link — the switch's note says so while the menu is open, before the leave.
wireLangSwitch(window, { linkFor: (href) => shareUrl(href, doc), loses: () => !!art || doc.font.src === 'upload' });
mountLangOffer(window, { linkFor: (href) => shareUrl(href, doc) });   // the offer's link carries the design too (B1)
const worker = createWorkerClient({ url: window.__WORKER_URL__, recycleAfter: 300, onFatal: () => { $('#issues').innerHTML = `<li class="error">${S.fatal}</li>`; } });
const preview = createPreview($('#preview'));
hit('name-tag', 'open');
hit('name-tag', 'choice', `lang:${pageLanguage().id}`); // R25: the page's OWN address's language, never the browser's

// ---- the drawing (spec 2026-10-08 name-tag-svg). `art` is the uploaded outline as numbers: it lives here and nowhere
// else, and is gone on reload. doc.icon.on says the DESIGN has a drawing (set when a file was read, cleared only by
// "เอารูปออก"); a drawing is ON the tag only with both. The file's text is a local of readFile() and goes to the worker
// and nowhere else: not the DOM, the URL, storage, a log, a beacon, a file name. ----
let art = null, artSkipped = null, shownSkipped = null, uploadError = null, reading = false, linkNotice = null, pickSeq = 0, builds = 0, queued = false;
const SVG_MAX_BYTES = 1048576;
// buildTimeoutMs / stillBuildingMs: the two numbers of a build without a drawing (ruling R58) — read when a build is sent;
// a test shortens them and reads them back. `stills`: how many times the still-building line was shown.
window.__NAMETAG__ = { artTimeoutMs: 10000, artBuildTimeoutMs: 8000, artExportTimeoutMs: 20000, buildTimeoutMs: 120000, stillBuildingMs: 10000, stills: 0, picks: 0,
  get builds() { return builds; }, state: () => ({ on: doc.icon.on, hasArt: !!art, reading, text: doc.text, queued }) };   // for the tests: finished builds, and what the page holds
if (doc.icon.on) linkNotice = S.linkNeedsIcon;                       // a link says a drawing is meant, and never carries it
const artFor = (d) => (d.icon.on ? art : null);

// ---- static copy ----
$('#title').textContent = S.title; $('#g-text').textContent = S.text; $('#l-text').textContent = S.text; $('#text-hint').textContent = S.textHint; $('#l-linegap').textContent = S.lineGap; $('#l-font').textContent = S.font;
$('#l-cap').textContent = S.cap; $('#l-spacing').textContent = S.spacing; $('#g-layers').textContent = S.layers; $('#l-count').textContent = S.layerCount;
$('#g-loop').textContent = S.loop; $('#l-hole').textContent = S.hole; $('#l-ringw').textContent = S.ringW; $('#g-print').textContent = S.print; $('#l-lh').textContent = S.layerHeight;
$('#g-icon').textContent = S.icon; $('#pick').textContent = S.pick; $('#unpick').textContent = S.unpick; $('#l-iconsize').textContent = S.iconSize; $('#l-icongap').textContent = S.iconGap;
$('#dl3mf').textContent = S.dl3mf; $('#dlstl').textContent = S.dlstl; $('#share').textContent = S.share; $('#busy').textContent = S.updating;
// ---- the font control (unify-ui ship 4a: @3dp/ui/font-control — the button, the weight select, the line for a family not
// yet print-tested, and the sheet behind the button). curated.json is the same file the worker reads; the button stays
// disabled until the list is here, so a tap can never open an empty sheet. A record that IS an entry of the list is held as
// the entry's own (R95, asListed): one font, one record. ----
let curated = [], raisedFrom = null;
const familyOf = (id) => (curated.find(f => f.id === id) || {}).family || id;   // a Google font's id IS its family (spec §4.3)
const fonts = createFontControl({ root: $('#font-pick'), S, core: PICKER_CORE, getFont: () => doc.font, getText: () => doc.text, onPick: pickFont,
  // the weight select: the record of that weight (a bundled family's own weight is the bundled record again)
  onWeight: (font) => { doc.font = font; notice(''); raisedFrom = null; fonts.setFont(); schedule(); } });
fonts.setFont();
fetch(asset('fonts/curated.json')).then(r => r.json()).then(list => {
  curated = list;
  const held = asListed(doc.font, curated);
  if (fontKey(held) !== fontKey(doc.font)) { doc.font = held; schedule(); }   // a link's record of a list entry, by another name (R95)
  fonts.setList(list);
}).catch(() => { $('#issues').innerHTML = `<li class="error">${S.errorCode['font-load']}</li>`; });
// A font is picked: from the sheet, or by the one-tap offer beside a report of characters the font lacks (below).
function pickFont(f) {
    doc.font = asListed({ src: f.src, id: f.id, variant: f.src === 'bundled' ? '' : f.variant, name: '' }, curated);
    fonts.setFont();
    // F6: a pick raises the letter height to the font's minCap only when the current value is lower; the
    // slider and its output follow (bind() alone sets them at start). A share link never changes its cap.
    // raisedFrom remembers the height a raise replaced, so a load failure (Task 6) can put it back.
    raisedFrom = f.minCap > doc.cap ? doc.cap : null;
    if (raisedFrom !== null) { doc.cap = f.minCap; capCtl.sync(); notice(S.notice.capRaised(f.family, f.minCap)); }
    else notice('');
    schedule();
}
// Beside a report of characters the font lacks: ONE family that has them, in one tap, with its size (unify-ui ruling
// R14) — by the script of the missing characters, and only while the font in use is a bundled one (one offer, no
// chain). A pick like any other: the letter height is raised to the family's smallest. Nothing is asked of any host
// before the tap.
function offerFont(li, chars) {
  const entry = offerFor(chars, curated, doc.font, pageLanguage().id);   // ship 3: a ja / zh page offers its own family first
  if (li && !entry && listMayHelp(chars, doc.font)) { const n = document.createElement('span'); n.className = 'ui-offer-note'; n.textContent = S.pickFromList; li.append(n); }   // R83
  if (!li || !entry) return;
  const b = document.createElement('button'); b.type = 'button'; b.className = 'ui-offer'; b.dataset.font = entry.id;
  b.textContent = S.useFont(entry.family, megabytes(entry.bytes));
  b.addEventListener('click', () => pickFont(entry));
  li.append(b);
}

// ---- segmented controls ----
function seg(el, options, labels, get, set) {
  el.innerHTML = options.map(o => `<button type="button" data-v="${o}">${labels[o]}</button>`).join('');
  const paint = () => el.querySelectorAll('button').forEach(b => b.classList.toggle('on', b.dataset.v === get()));
  el.addEventListener('click', e => { const b = e.target.closest('button'); if (!b) return; set(b.dataset.v); paint(); schedule(); });
  paint();
  return { sync: paint };      // repaint from the doc, for a change the pill did not make itself
}
seg($('#pos'), LIMITS.pos, S.pos, () => doc.loop.pos, v => { doc.loop.pos = v; });
seg($('#mode'), LIMITS.modes, S.mode, () => doc.print.mode, v => { doc.print.mode = v; });

// ---- form <-> doc ----
const bind = (id, get, set, fmt = (v) => v) => {
  const el = $('#' + id), out = el.parentElement.querySelector('output');
  const sync = () => { el.value = get(); if (out) out.value = fmt(get()); };
  sync();
  el.addEventListener('input', () => { set(el.value); if (out) out.value = fmt(get()); schedule(); });
  return { sync };
};
// Text is a <textarea> (T8/T9): maxlength can't clamp lines, so clampText() rewrites the value in place
// (lines beyond LIMITS.lines, chars beyond LIMITS.textMax per line — .length, not a grapheme count, to
// match normalize()'s own UTF-16 str().slice()) and restores the caret. Skipped mid-composition
// (isComposing) so a half-typed IME cluster is never cut; clamped once instead on compositionend.
const textEl = $('#text'); textEl.value = doc.text;
function clampText(el) {
  const ls = el.value.split('\n');
  if (ls.length <= LIMITS.lines && ls.every(l => l.length <= LIMITS.textMax)) return;
  const caret = el.selectionStart;
  el.value = ls.slice(0, LIMITS.lines).map(l => l.slice(0, LIMITS.textMax)).join('\n');
  const pos = Math.min(caret, el.value.length); el.setSelectionRange(pos, pos);
}
textEl.addEventListener('input', (e) => { if (!e.isComposing) clampText(textEl); doc.text = textEl.value; syncMulti(); schedule(); });
textEl.addEventListener('compositionend', () => { clampText(textEl); doc.text = textEl.value; syncMulti(); schedule(); });
// #multi (align pills + line-gap slider) only matters once there's more than one line to align/space.
function syncMulti() { $('#multi').classList.toggle('hidden', doc.text.split('\n').filter(l => l.trim()).length < 2); }
seg($('#dir'), LIMITS.dirs, S.dir, () => doc.dir, v => { doc.dir = v; paintAlignLabels(); });
seg($('#align'), LIMITS.aligns, S.align.h, () => doc.align, v => { doc.align = v; });
// seg() renders each button's label once — relabel in place on a dir change rather than calling seg()
// again, which would re-render the buttons and double the click listener.
function paintAlignLabels() { $('#align').querySelectorAll('button').forEach(b => { b.textContent = S.align[doc.dir][b.dataset.v]; }); }
paintAlignLabels();
syncMulti();
$('#lh').innerHTML = LIMITS.layerHeights.map(v => `<option value="${v}">${v}${S.mm}</option>`).join('');
const capCtl = bind('cap', () => doc.cap, v => { doc.cap = +v; }, v => v + S.mm);
$('#cap').addEventListener('input', () => { notice(''); raisedFrom = null; });   // the user took over the height: the notice is stale and a later fall-back must not undo their value
// One line under the height slider: why the cap moved (this task) or why the font fell back (Task 6). '' hides it.
function notice(text) { const el = $('#notice'); el.textContent = text; el.classList.toggle('hidden', !text); }
// The drawing's controls: the place pill and two sliders, shown while the design has a drawing.
const iconPos = seg($('#icon-pos'), LIMITS.iconPos, S.iconPos, () => doc.icon.pos, (v) => { doc.icon.pos = v; });
const iconSize = bind('iconsize', () => doc.icon.size, (v) => { doc.icon.size = +v; }, S.pct);
const iconGap = bind('icongap', () => doc.icon.gap, (v) => { doc.icon.gap = +v; }, (v) => v + S.mm);
const iconCtl = { sync() { iconPos.sync(); iconSize.sync(); iconGap.sync(); } };
bind('spacing', () => doc.spacing, v => { doc.spacing = +v; }, v => v + S.mm);
bind('linegap', () => doc.lineGap, v => { doc.lineGap = +v; }, v => v + S.mm);
bind('hole', () => doc.loop.hole, v => { doc.loop.hole = +v; }, v => v + S.mm);
bind('ring', () => doc.loop.ring, v => { doc.loop.ring = +v; }, v => v + S.mm);
bind('lh', () => doc.print.layerHeight, v => { doc.print.layerHeight = +v; });
$('#count').value = doc.layers.length;
$('#count').addEventListener('input', () => { const n = +$('#count').value, d = defaultDoc().layers, text = doc.layers[doc.layers.length - 1];
  const rings = doc.layers.slice(0, -1).slice(0, n - 1);
  while (rings.length < n - 1) rings.push({ ...d[rings.length] });
  doc.layers = [...rings, text];                                   // the text band keeps its colour and height across count changes
  renderColours(); schedule(); });
// ---- the colours (unify-ui ship 4b: @3dp/ui/colours — moved there from here, D3): one row per band, its swatch with the
// colour's name under it, every filament and brand colour plus the visitor's own in one tap; the colour sets as strips. The
// band's width and height sliders are this tool's own controls in its row (extra): the shell's rows, each input an id of its own
// (the shared spec reads every slider of a .ui-row at both ends, by id). An own colour is stored in the band as
// any colour (R89); the bands are the slots, so it changes nothing in the 3MF but swap mode's colour-change list (R99). ----
const layerLabel = (b, n) => b === 0 ? S.base : b === n - 1 ? S.textLayer : S.ring + ' ' + (n - 1 - b);
function layerExtra(part) {
  const b = +part.key, l = doc.layers[b], n = doc.layers.length, box = document.createElement('div');
  box.className = 'layer-sliders';
  box.innerHTML = `${b < n - 1 ? `<label class="ui-row"><span>${S.width}</span><input type="range" id="layer-w-${b}" data-k="w" min="${LIMITS.w[0]}" max="${LIMITS.w[1]}" step="0.1" value="${l.w}"><output data-o="w">${l.w}${S.mm}</output></label>` : ''}
    <label class="ui-row"><span>${S.height}</span><input type="range" id="layer-h-${b}" data-k="h" min="${LIMITS.h[0]}" max="${LIMITS.h[1]}" step="0.1" value="${l.h}"><output data-o="h">${l.h}${S.mm}</output></label>`;
  box.addEventListener('input', (e) => { const k = e.target.dataset.k; if (!k) return;
    doc.layers[b][k] = +e.target.value; box.querySelector(`output[data-o="${k}"]`).textContent = doc.layers[b][k] + S.mm; schedule(); });
  return box;
}
const colours = createColours({ root: $('#colours'), S, core: COLOUR_CORE, looksLabel: S.looks, extra: layerExtra,
  onChange: (key, hex) => { doc.layers[+key].color = hex; renderColours(); schedule(); },
  onLook: (name) => { const look = LOOKS.find((l) => l.name === name); if (!look) return; doc.layers = applyLook(doc.layers, look); renderColours(); schedule(); } });
// a colour set's strip shows what a tap gives THIS tag: its colours as applyLook maps them onto the bands there are
function renderColours() {
  const n = doc.layers.length;
  colours.set(doc.layers.map((l, b) => ({ key: String(b), label: layerLabel(b, n), value: l.color })),
    LOOKS.map((look) => ({ key: look.name, name: look.name, colours: Object.fromEntries(applyLook(doc.layers, look).map((l, b) => [String(b), l.color])) })));
}
renderColours();

// ---- build loop: 150 ms debounce, latest wins ----
let timer = null, seq = 0;
function schedule() { clearTimeout(timer); queued = true; timer = setTimeout(build, 150); }
let lastGood = null;   // the font of the last successful build: where a failed load falls back to (spec §7 offline)
// ---- the drawing's page half: the group's four states, the thumbnail, the upload ----
// false inside a closed <details> too; checkVisibility() is missing before Safari 17.4 / iOS 17.4, offsetParent is the fallback (as in the coaster)
const shown = (el) => (typeof el.checkVisibility === 'function' ? el.checkVisibility() : el.offsetParent !== null);
function reveal(el) {
  if (!shown(el)) return;
  const r = el.getBoundingClientRect();
  if (r.top < 0 || r.bottom > $('.bar').getBoundingClientRect().top) el.scrollIntoView({ block: 'nearest' });
}
// The group's four states (spec, "The page"): off · on with the drawing in hand · on from a link · on, dropped by a clock.
function renderIcon() {
  const on = doc.icon.on;
  $('#unpick').classList.toggle('hidden', !on);
  $('#icon-controls').classList.toggle('hidden', !on);
  $('#thumb').classList.toggle('hidden', !art);
  $('#pick').disabled = reading; $('#unpick').disabled = reading;
  const sk = shownSkipped || {};
  $('#skipped').innerHTML = Object.keys(sk).filter((k) => sk[k] > 0 && S.skipped[k]).map((k) => `<li data-k="${esc(k)}">${esc(S.skipped[k](sk[k]))}</li>`).join('');
  const note = $('#upload-note');
  const line = reading ? S.reading
    : uploadError ? uploadError + (art ? ' ' + S.keptOld : '')
    : art ? S.artKept
    : on ? S.linkNeedsIcon
    : isLineBrowser(navigator.userAgent) ? S.needFileLine : S.needFile;
  note.textContent = line; note.classList.toggle('error', !!uploadError && !reading);
  const ln = $('#link-notice'); ln.textContent = linkNotice || ''; ln.classList.toggle('hidden', !linkNotice);
}
// The art's rings, drawn from the numbers (never from the file): art is y up and centred on its box, a canvas is y down.
const THUMB = [96, 64];
function drawThumb() {
  const c = $('#thumb'), dpr = Math.min(window.devicePixelRatio || 1, 2);
  c.width = THUMB[0] * dpr; c.height = THUMB[1] * dpr;             // the CSS box (main.css) times the screen's density
  c.setAttribute('aria-label', S.thumb);
  const ctx = c.getContext('2d');
  ctx.clearRect(0, 0, c.width, c.height);
  if (!art || !(art.w > 0 && art.h > 0)) return;
  const pad = 6 * dpr, k0 = Math.min((c.width - 2 * pad) / art.w, (c.height - 2 * pad) / art.h), cx = c.width / 2, cy = c.height / 2;
  ctx.beginPath();
  let k = 0;
  for (const n of art.rings) {
    for (let j = 0; j < n; j++, k += 2) {
      const x = cx + k0 * art.points[k], y = cy - k0 * art.points[k + 1];
      if (j === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath();
  }
  ctx.fillStyle = getComputedStyle(c).color;      // the page's text colour
  ctx.fill('evenodd');
}
// The limit is for the engine's work on the file. callOnce() waits for the worker that is current now, posts the file to it,
// and starts the clock when that worker says it has STARTED the request: the wait for wasm and fonts, or for a build in
// front of it, is not the file's doing. Replies of its own: `replaced` — that worker was swapped; `timeout`; `fatal`.
const askArt = (svg) => worker.callOnce({ type: 'art', svg }, { limitMs: window.__NAMETAG__.artTimeoutMs });
// → { art, skipped } or { error: message, skipped? }.
async function readFile(file) {
  if (!/\.svg$/i.test(file.name) && file.type !== 'image/svg+xml') return { error: S.notSvg };   // a phone's picker may hand over a real SVG with no extension, but a type
  if (file.size > SVG_MAX_BYTES) return { error: S.svgTooBig };
  let svg;
  try { svg = await file.text(); } catch { return { error: S.readFail }; }
  let r = await askArt(svg);
  if (r.error && r.error.code === 'replaced') r = await askArt(svg);   // a build's engine fault swapped the worker under this call: start over, once — the new worker, a new clock
  const code = r.ok ? null : r.error && r.error.code;
  if (code === 'engine' || code === 'timeout') {
    // This file broke the engine, or kept it past the limit. A fresh worker; the file is not sent again; the page builds as before.
    worker.replace(); schedule();
    return { error: S.svgTooBig };
  }
  // The reader's three verdicts name the file's fault. Anything else (`internal`, a second `replaced`) says nothing
  // about the file, which may be a good one: the message asks to try again and does not call it a bad SVG.
  if (code === 'fatal') return { error: S.fatal };                // the worker cannot work: no file can be read on this page
  const verdict = { 'svg-too-big': 'svgTooBig', 'svg-empty': 'svgEmpty', 'svg-bad': 'svgBad' };
  if (!r.ok) return { error: S[Object.hasOwn(verdict, code) ? verdict[code] : 'readFail'], skipped: r.skipped };
  return { art: r.art, skipped: r.skipped };
}
async function pickFile(file) {
  const my = ++pickSeq;
  reading = true; renderIcon();
  let r;
  try { r = await readFile(file); } catch { r = { error: S.readFail }; }      // whatever happens, the button comes back
  reading = false;
  if (my !== pickSeq) { window.__NAMETAG__.picks++; renderIcon(); return; }   // the page moved on while the file was read: nothing of it is used
  if (r.art) {
    art = r.art; artSkipped = shownSkipped = r.skipped || null; uploadError = null; linkNotice = null;
    doc.icon.on = true;                                                       // here, and nowhere else: the design has a drawing
    iconCtl.sync(); seq++; schedule();                                        // seq++: a build already in flight no longer says what is on the tag
  } else { uploadError = r.error; shownSkipped = r.skipped || artSkipped; }   // a refused file changes nothing but the message: the earlier art stays
  window.__NAMETAG__.picks++;
  renderIcon();
  if (r.art) { drawThumb(); reveal($('#upload-row')); } else reveal($('#upload-note'));
}
$('#pick').addEventListener('click', () => $('#file').click());
$('#file').addEventListener('change', () => {
  const file = $('#file').files[0];
  $('#file').value = '';             // the input forgets the file (and its name); the same file can be picked again
  if (file && !reading) pickFile(file);
});
$('#unpick').addEventListener('click', () => {
  pickSeq++; seq++; art = null; artSkipped = shownSkipped = null; uploadError = null; linkNotice = null;
  doc.icon = { on: false, pos: 'left', size: 1, gap: 2 };
  iconCtl.sync(); renderIcon(); drawThumb(); schedule();     // the text field is not touched: a blank text builds the default text, the field keeps its placeholder
});

// A request that carries the drawing has a clock, counted from the worker's `started` (spec D11): the engine cannot be
// interrupted, and no count of points bounds one boolean in it. A clock that fires, or a second engine fault, is the
// doing of the drawing THAT REQUEST carried: the worker is replaced and only that drawing is dropped. A request without
// a drawing had no clock until unify-ui ship 2 (ruling R22): a BUILD without one now has a clock on the worker's own
// work, from its `started` — which the worker posts once the font is in hand, so a font file on its way is never
// counted. It has TWO numbers (ruling R58; R48, one end at 10 s, is withdrawn): ordinary three-line Thai that fits a bed
// takes up to 25 s on the reference desktop — in the bridge search of packages/core/src/layers.js, which is `main`'s
// own and is not changed here — so an end at 10 s would have ended designs production builds.
//   stillBuildingMs (10 s): from then on the preview says the build is still on its way (build(), `#busy.still`):
//     not an error, no file button changes, gone when the build arrives.
//   buildTimeoutMs (120 s): the build is ended. Nothing is dropped: the worker is replaced and the page says so
//     (`build-timeout`), in view. 120 s is 4.8 times the slowest design measured that fits a bed.
function dropArt() {
  art = null; artSkipped = shownSkipped = null; uploadError = S.svgTooBig;    // doc.icon.on stays: the design still wants one
  $('#g-icon-box').open = true;                            // the reason must be seen: a closed group has no box to scroll to
  renderIcon(); drawThumb(); schedule(); reveal($('#upload-note'));    // schedule() first: nothing thrown by reveal() may stop the rebuild
}
async function callWorker(msg, opts = {}) {
  const limit = () => window.__NAMETAG__[msg.type === 'export' ? 'artExportTimeoutMs' : 'artBuildTimeoutMs'];
  const plainBuild = msg.type === 'build' && !msg.art;
  const ask = () => (msg.art ? worker.callOnce(msg, { limitMs: limit(), ...opts }) : plainBuild ? worker.callOnce(msg, { limitMs: window.__NAMETAG__.buildTimeoutMs, ...opts }) : worker.call(msg, opts));
  const code = (r) => (r.error ? r.error.code : null);
  const gone = { ok: false, error: { code: 'timeout' } };
  let r, faulted = false, reasked = false;
  for (;;) {
    r = await ask();
    if (code(r) === 'engine' && !faulted) { faulted = true; worker.replace(); continue; }            // a dead engine: a fresh worker, the same request once more
    if (code(r) === 'replaced' && !reasked && (!msg.art || msg.art === art)) { reasked = true; continue; }   // lost to a swap: asked again, once
    break;
  }
  // a second engine fault on a request WITHOUT a drawing: that worker's engine is dead too (the loop above replaced the first one,
  // which answered the same request again); end it, so that the next request — a picked file — meets a fresh one and is not told "too detailed"
  if (code(r) === 'engine' && !msg.art) worker.replace();      // with a drawing, the branch below replaces it: once, not twice
  // the build's clock fired: the engine cannot be interrupted, so the worker is ended and a new one started; build() shows the reason
  if (plainBuild && code(r) === 'timeout') { worker.replace(); return { ok: false, error: { code: 'build-timeout' } }; }
  if (!msg.art) return r;
  if (code(r) === 'timeout' || code(r) === 'engine') {
    worker.replace();
    if (msg.art === art) dropArt(); else schedule();       // a newer file read meanwhile stays
    return gone;
  }
  // an export that lost its worker because ANOTHER request's clock fired, and whose drawing was dropped with it:
  // the upload row already says why
  if (code(r) === 'replaced' && msg.type === 'export' && msg.art !== art) return gone;
  return r;
}
// The still-building line (R58): the busy mark, in the preview, with a sentence in it — see callWorker's comment.
let stillTimer = null;
function stillOff() { clearTimeout(stillTimer); stillTimer = null; $('#busy').classList.remove('still'); }
// A NEW error line is brought into view, as the other four text tools do (#issues is the panel's last element, under
// five cards): once when an error code appears that the line did not hold — not on every build, so a visitor who goes
// on typing is not scrolled again for the same reason.
let shownErrors = [];
function saidErrors(codes) {
  const fresh = codes.some((c) => !shownErrors.includes(c));
  shownErrors = codes;
  if (fresh) reveal($('#issues'));
}
async function build() {
  queued = false;
  const my = ++seq; const sent = doc.font;   // the font THIS build actually requested — onPick assigns doc.font a NEW
  stillOff();                                                                   // a newer build takes the line down: it counts from its own start
  $('#busy').textContent = S.updating; $('#busy').classList.remove('hidden');   // object, so `sent` still names the old one even if the user picks again before the reply lands
  const sentArt = artFor(doc);
  let progressSeen = false;
  const reply = await callWorker({ type: 'build', doc, art: sentArt }, { onProgress: (p) => {
    if (my !== seq) return;
    window.__lastProgress = { received: p.received, total: p.total };
    $('#busy').textContent = S.loadingFont(p.received / 1048576, p.total / 1048576);
    // A font file is on its way: the line that says so is in the preview, and the visitor may be at the foot of the
    // page (the offer's button is). It is brought into view once, when it first shows (unify-ui ruling R65).
    if (!progressSeen) { progressSeen = true; reveal($('#busy')); }
  }, onStarted: () => {
    // The worker is on this build now, its font in hand. After the first number the busy mark says so (R58) — only
    // for a build without a drawing (one with a drawing is ended by its own clock before that).
    if (my !== seq || sentArt) return;
    stillOff();
    const after = window.__NAMETAG__.stillBuildingMs;
    if (!(after > 0 && after <= 2147483647)) return;
    stillTimer = setTimeout(() => { if (my !== seq) return; $('#busy').textContent = S.stillBuilding; $('#busy').classList.add('still'); window.__NAMETAG__.stills++; }, after);
  } });
  if (my !== seq) return;
  try {
    stillOff();
    $('#busy').classList.add('hidden');
    window.__lastBuild = reply;
    const code = reply.error && reply.error.code;
    if (code === 'timeout') return;                          // dropArt() has written the reason at the upload row and scheduled the next build
    if (code === 'replaced') { schedule(); return; }         // asked again once and lost again: never shown as an error
    if (!reply.ok) {
      if (reply.error.code === 'font-load' && lastGood && fontKey(lastGood) !== fontKey(sent)) {
        // The pick could not load: keep the previous font (still the worker's face, so the rebuild is quick), say so once,
        // and mark the row for the next open. A share link that names an unloadable font has no previous font → error path.
        // Revert only if the live pick is still the one that failed (`sent`) — a newer pick made during the debounce
        // must never be clobbered by a stale failure (Important #3).
        const failedId = sent.id;
        if (fontKey(doc.font) === fontKey(sent)) {
          doc.font = { ...lastGood }; fonts.setFont();
          if (raisedFrom !== null) { doc.cap = raisedFrom; capCtl.sync(); raisedFrom = null; }   // undo the pick's cap raise (Task 4)
        }
        fonts.markFailed(fontKey(sent)); notice(S.notice.loadFailed(familyOf(failedId)));
        schedule(); reveal($('#notice')); return;                 // schedule() first: nothing thrown by reveal() may stop the rebuild
      }
      $('#issues').innerHTML = `<li class="error">${esc(S.errorCode[reply.error.code] || reply.error.code)}</li>`; $('#dl3mf').disabled = $('#dlstl').disabled = true;
      saidErrors([reply.error.code]); return;
    }
    lastGood = { ...reply.doc.font };
    worker.countGeneration(); worker.recycleIfDue();
    preview.setBands(reply.bands);
    $('#size').textContent = S.size(reply.bbox.w, reply.bbox.h, reply.bbox.z);
    $('#issues').innerHTML = reply.issues.map(i => `<li class="${esc(i.level)}">${esc(S.issue[i.code](i.params))}</li>`).join('');
    const lacking = reply.issues.findIndex(i => i.code === 'missing-glyph');
    if (lacking >= 0) offerFont($('#issues').children[lacking], reply.issues[lacking].params.chars);
    // The z table is the swap-mode guide (spec §7/§8) — blank in multi mode. Reply's doc, not the live
    // one: the rows must describe the build that produced them, not a doc edited mid-debounce.
    $('#ztable').innerHTML = reply.doc.print.mode === 'swap'
      ? reply.zTable.slice(1).map(r => `<li>${esc(S.zTable(reply.bands[r.band].color, r.z0, Math.round(r.z0 / reply.doc.print.layerHeight)))}</li>`).join('')
      : '';
    const blocked = reply.issues.some(i => i.level === 'error');
    $('#dl3mf').disabled = blocked; $('#dlstl').disabled = blocked;
    saidErrors(reply.issues.filter((i) => i.level === 'error').map((i) => i.code));
  } finally {
    if (my === seq) { builds++; renderIcon(); drawThumb(); }   // the group is painted, and the count moves, after every build that was not superseded
  }
}
async function download(format) {
  const sent = normalize(doc), sentArt = artFor(doc);
  const r = await callWorker({ type: 'export', doc, art: sentArt, format });
  if (!r.ok) {
    if (r.error.code === 'timeout') return;                  // the drawing was dropped and the upload row says why, in view
    const code = r.error.code === 'replaced' ? 'internal' : r.error.code;      // the raw word `replaced` never reaches the page
    $('#issues').innerHTML = `<li class="error">${S.exportFail} (${esc(S.errorCode[code] || code)})</li>`; return;
  }
  // The anchor is attached before click(): a detached <a download> is ignored by Safari/iOS, the
  // app's primary target (the idiom is pet-tags' src/main.js download()).
  const href = URL.createObjectURL(new Blob([r.bytes], { type: r.mime }));
  const a = document.createElement('a'); a.href = href; a.download = r.filename;
  document.body.appendChild(a); a.click(); a.remove();
  // Choices are sent once per download (spec §3): they measure what actually gets printed, not
  // every tap. `doc.print.mode` is the real field name (the plan's sketch said `doc.mode`); its
  // values ('multi'/'swap') already satisfy the registry's `^(multi|swap)$`, so no registry change
  // was needed here.
  hit('name-tag', 'download', format);
  hit('name-tag', 'choice', `look:${lookLabel(doc.layers, LOOKS, applyLook)}`);
  hit('name-tag', 'choice', `font:${doc.font.src === 'upload' ? 'upload' : doc.font.id}`);
  hit('name-tag', 'choice', `layers:${doc.layers.length}`);
  hit('name-tag', 'choice', `mode:${doc.print.mode}`);
  hit('name-tag', 'choice', `layout:${doc.dir}${Math.min(3, doc.text.split('\n').filter(l => l.trim()).length) || 1}`);
  hit('name-tag', 'choice', `icon:${r.icon && !r.icon.gone ? sent.icon.pos : 'none'}`);   // r.icon is the export's own build: a drawing gone at its size is `none`
  setTimeout(() => URL.revokeObjectURL(href), 5000);
}
$('#dl3mf').addEventListener('click', () => download('3mf'));
$('#dlstl').addEventListener('click', () => download('stl'));
$('#share').addEventListener('click', async () => {
  const u = new URL(shareUrl(location.href, doc));
  try { await navigator.clipboard.writeText(u.toString()); hit('name-tag', 'share'); $('#share').textContent = S.copied; setTimeout(() => { $('#share').textContent = S.share; }, 1500); } catch { hit('name-tag', 'share'); prompt('', u.toString()); }
  if (doc.icon.on) { linkNotice = S.shareNoIcon; renderIcon(); reveal($('#link-notice')); }    // the link never carries the file: the sender is told, in view
});
renderIcon();                                    // before the first build: a link's notice must not wait for it
build();
if (linkNotice) reveal($('#link-notice'));    // after build(): nothing thrown by reveal() may stop the first build
